import Component from "vue-class-component";
import { VueBase } from "../../../Controls/VueBase";
import moment from "moment";
import { mapState } from "vuex";
import DropDownSelect, { MultiSelectOption } from "../../../../shared/components/dropDownSelect/dropDownSelect";
import { EtatAbsencesEnum } from "../../../../dto/GenApi.iNotPaie.Dto/EtatAbsencesEnum";
import { TypeAbsenceSalarieDto } from "../../../../dto/GenApi.iNotPaie.Dto/TypeAbsenceSalarieDto";

export interface IListFilters {
	statut: EtatAbsencesEnum[],
	dateDebut: Date | null,
	dateFin: Date | null,
	type: string[]
}

export interface IListFiltersBack {
	statut: EtatAbsencesEnum[],
	dateDebut: string | null,
	dateFin: string | null,
	type: string[],
}

@Component({
	components: {
		DropDownSelect: require("../../../../shared/components/dropDownSelect/dropDownSelect.vue.html")
	},
	computed: {
		...mapState("AbsencesStore", ["storeTypesAbsencePosableSalarie"])
	}
})
export default class FilterAbsencesComponent extends VueBase {
	public listeStatutFiltre: MultiSelectOption[] = [{
    	value: "0",
    	label: "En attente de validation"
	}, {
    	value: "1",
    	label: "Validé"
	}, {
    	value: "2",
    	label: "Refusé"
	}, {
    	value: "3",
    	label: "En attente d'annulation"
	}, {
    	value: "4",
    	label: "Annulé"
	}, {
    	value: "5",
    	label: "Pris"
	}];

	public listeTypeFiltre: MultiSelectOption[] = []; // Liste des types d'absences pour le filtre

	public storeTypesAbsencePosableSalarie!: TypeAbsenceSalarieDto[]; // Mappé depuis le store Vuex

	public dateDebut: Date = moment().startOf("day").toDate();
	public dateFin: Date | null = null;

	public dataIsReady = false; // Indique si les données sont prêtes (types d'absences chargés)

	public listFilters: IListFilters = {
		statut: [],
		dateDebut: moment().startOf("day").toDate(),
		dateFin: null,
		type: []
	}

	public async mounted(): Promise<void> {
		this.dataIsReady = false;

		try {
			await this.$store.dispatch("AbsencesStore/initializeTypesAbsences");
		} finally {
			this.setTypesAbsenceFilter();
			this.dataIsReady = true;
		}
	}

	/**
	 * Initialise la liste des types d'absences pour le filtre.
	 */
	public setTypesAbsenceFilter(): void {
		this.listeTypeFiltre = [];
		this.storeTypesAbsencePosableSalarie.forEach((typeAbsence) => {
			this.listeTypeFiltre.push({
				value: typeAbsence.Type as string,
				label: typeAbsence.Libelle as string
			});
		});

		// Ajout de l'option "Autres" pour les types d'absences non posables par le salarié
		this.listeTypeFiltre.push({
			value: "AUTRES",
			label: "Autres"
		});
	}

	/**
	 * Méthode appelée lorsque la liste des statuts sélectionnés change.
	 * @param listStatutsSelectionnes
	 */
	public onStatutFilterChanged(listStatutsSelectionnes: string[]): void {
		const statutEtatMap: Record<string, EtatAbsencesEnum[]> = {
			0: [
				EtatAbsencesEnum.EnAttenteValidationGestionnaire,
				EtatAbsencesEnum.EnAttenteValidationResponsableNiveau_1,
				EtatAbsencesEnum.EnAttenteValidationResponsableNiveau_2
			],
			1: [EtatAbsencesEnum.Valide, EtatAbsencesEnum.Calcule],
			2: [EtatAbsencesEnum.Refuse],
			3: [
				EtatAbsencesEnum.EnAttenteAnnulationGestionnaire,
				EtatAbsencesEnum.EnAttenteAnnulationResponsableNiveau_1,
				EtatAbsencesEnum.EnAttenteAnnulationResponsableNiveau_2
			],
			4: [EtatAbsencesEnum.Annule],
			5: [EtatAbsencesEnum.Cloture]
		};

		const originalStatut = [...this.listFilters.statut]; // Copie de l'état original pour comparaison

		this.listFilters.statut = [];

		if (listStatutsSelectionnes.length > 0) {
			listStatutsSelectionnes.forEach((statut) => {
				statutEtatMap[statut].forEach((etat) => {
					this.listFilters.statut.push(etat);
				});
			});
		}

		if (!this.isIdentique(this.listFilters.statut, originalStatut)) {
			this.$emit("filterChanged", this.listFilters);
		}
	}

	/**
	 * Méthode appelée lorsque la liste des types sélectionnés change.
	 * @param listTypesSelectionnes
	 */
	public onTypeFilterChanged(listTypesSelectionnes: string[]): void {
		if (!this.isIdentique(this.listFilters.type, listTypesSelectionnes)) {
			this.listFilters.type = listTypesSelectionnes;

			this.$emit("filterChanged", this.listFilters);
		}
	}

	/**
	 * Méthode appelée lorsque la date de début ou de fin change.
	 */
	public onDateChange(): void {
		if (this.dateFin && this.dateDebut > this.dateFin) {
			this.dateFin = null;
			this.listFilters.dateFin = null;
		} else {
			this.listFilters.dateFin = this.dateFin;
		}

		this.listFilters.dateDebut = this.dateDebut;
		this.$emit("filterChanged", this.listFilters);
	}

	/**
	 * Vérifie si les filtres sont actifs.
	 * @returns boolean
	 */
	public get hasActiveFilters(): boolean {
		const defaultDateDebut = moment().startOf("day");
		const hadDefaultDateDebut = moment(this.dateDebut).isSame(defaultDateDebut, "day");
		const hadNoDateFin = this.dateFin == null;
		const hadNoType = this.listFilters.type.length === 0;
		const hadNoStatut = this.listFilters.statut.length === 0;
		const nothingToReset = hadDefaultDateDebut && hadNoDateFin && hadNoType && hadNoStatut;

		if (!nothingToReset) {
			return true;
		}

		return false;
	}

	/**
	 * Réinitialise les filtres.
	 * @returns void
	 */
	public resetFilters(): void {
		if (this.hasActiveFilters) {
			// Réinitialise les propriétés locales liées aux v-model des date pickers
			this.dateDebut = moment().startOf("day").toDate();
			this.dateFin = null;

			// Réinitialise les dropdowns
			const typeDropdown = this.$refs.typeDropdown as DropDownSelect;
			const statutDropdown = this.$refs.statutDropdown as DropDownSelect;
			if (typeDropdown) {
				typeDropdown.reset();
			}
			if (statutDropdown) {
				statutDropdown.reset();
			}

			// Réinitialise le filtre
			this.listFilters = {
				statut: [],
				dateDebut: moment().startOf("day").toDate(),
				dateFin: null,
				type: []
			};

			// Émet l'événement si le filtre a été modifié
			this.$emit("filterChanged", this.listFilters);
		}
	}

	/**
	 * Options pour le sélecteur de date.
	 * Désactive les dates antérieures à la date de début.
	 */
	public get pickerOptions(): any {
		return {
			disabledDate: (date: Date) :boolean => {
				return date < this.dateDebut;
			},
			firstDayOfWeek: 1 // Commence la semaine le lundi
		};
	}

	/**
	 * Compare deux tableaux à une entrée pour vérifier s'ils sont identiques.
	 * @param arrA - Premier tableau à comparer
	 * @param arrB - Deuxième tableau à comparer
	 * @returns
	 */
	private isIdentique(arrA: any[], arrB: any[]): boolean {
		return arrA.length === arrB.length &&
			arrA.every((val, idx) => val === arrB[idx]);
	}
}



// WEBPACK FOOTER //
// ./src/components/gestionDesAbsencesV2/dashboardConge/filterAbsences/filterAbsences.ts